import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import UploadPopup from "./UploadResume";
import { getApiUrl } from "../../utils/api";
import { Badge } from "@/Components/ui/badge";
import { Button } from "@/Components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/Components/ui/table";
import { FileText, Trash2, Clock, AlertTriangle, Search } from "lucide-react";
import { Input } from "@/Components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/Components/ui/dialog";

const ROLE_STATUS_LABELS = {
  active: "Active",
  open: "Active",
  on_hold: "On Hold",
  hold: "On Hold",
  closed: "Closed",
  filled: "Filled",
  draft: "Draft",
  inactive: "Inactive",
  cancelled: "Cancelled",
  canceled: "Cancelled",
};

const ROLE_STATUS_BADGE_CLASS = {
  active: "text-blue-600 border-blue-200 bg-blue-50",
  open: "text-blue-600 border-blue-200 bg-blue-50",
  on_hold: "text-amber-700 border-amber-200 bg-amber-50",
  hold: "text-amber-700 border-amber-200 bg-amber-50",
  closed: "text-rose-700 border-rose-200 bg-rose-50",
  filled: "text-rose-700 border-rose-200 bg-rose-50",
  draft: "text-slate-600 border-slate-200 bg-slate-50",
  inactive: "text-slate-600 border-slate-200 bg-slate-50",
  cancelled: "text-rose-700 border-rose-200 bg-rose-50",
  canceled: "text-rose-700 border-rose-200 bg-rose-50",
};

const getJobRequirementStatus = (job) =>
  String(
    job?.requirement_status || job?.requirementStatus || job?.status || "active"
  )
    .trim()
    .toLowerCase();

const formatJobRequirementStatus = (job) => {
  const status = getJobRequirementStatus(job);
  return ROLE_STATUS_LABELS[status] || status.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

const JobsTable = ({ selectedCompany, onJobSelect, setSelectedJob, setActiveMain, companyEmail }) => {
  const location = useLocation();
  const jobID = new URLSearchParams(location.search).get("jobID");
  const companyID = new URLSearchParams(location.search).get("companyID");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [jobsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [candidates, setCandidates] = useState([]);
  const navigate = useNavigate();
  const [jobId, setjobId] = useState(null);

  useEffect(() => {
    if (companyEmail) fetchJDs(companyEmail);
  }, [companyEmail]);

  // Reset to the first page whenever the search term changes, so results are
  // never hidden behind a now-out-of-range page number.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleJobClick = (job) => {
    setjobId(job.job_id);
    setSelectedJob(job);
    // Debug log removed — was leaking job object to console.
    navigate(`/all-candidates?companyID=${encodeURIComponent(companyID)}&jobID=${encodeURIComponent(job.job_id)}`, {
      state: { jobID: job.job_id, companyID: companyID },
    });
  };

  const getActionIconUrl = (idx) => {
    const urls = [
      "5e65a24a80c9538f7891b250878cb0a10188ef8860a73d06fc57a763ad1e4d1c",
      "a54464106c5f3adf98bfeda08b3f6bebbd8b4e7d970ba3effa762046772b0ee3",
      "3f056c95851d2ed3363ac48b650a813c35419401c97e66f51c8b765c61463203",
      "887ee278d711169193225de39a088542e8d9bc6fddf15d06ec29ef8e0eaaa573",
      "8ec4737935d2c13bbac895a54e9765961fb06a1bd7a045dc41a219b69ec3e0b1",
      "a6cf2e28566ed64f3ea20f2808602de3fa53951b105b2e2af65425735cc2b6d6",
    ];
    return urls[idx];
  };

  const fetchJDs = async (companyEmail) => {
    try {
      // Debug log removed — was leaking company email to console.
      const adminEmail = sessionStorage.getItem('admin_email') || localStorage.getItem('admin_email');
      let url = getApiUrl("/api/get_jd");
      if (companyEmail) {
        url += `?company_email=${encodeURIComponent(companyEmail)}`;
      }

      const response = await fetch(url, {
        method: "GET",
        credentials: 'include',
        headers: {
          "Content-Type": "application/json",
          'X-Admin-Email': adminEmail || ''
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch JDs");
      }

      const data = await response.json();
      setJobs(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching JDs:", error);
      setLoading(false);
    }
  };

  const fetchCandidates = async () => {
    try {
      const response = await fetch(getApiUrl(`/api/candidatelist?jobId=${jobId}`), {
        method: "GET",
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) throw new Error("Failed to fetch Candidates");

      const data = await response.json();
      setCandidates(data);
    } catch (error) {
      console.error("Error fetching Candidates:", error);
    }
  };

  const handleApplicantClick = (jobId) => {
    navigate(`/ranking?jobID=${encodeURIComponent(jobId)}`, {
      state: { jobID: jobId },
    });
    onJobSelect(jobId);
  };

  const handleUploadClick = (event, jobId) => {
    event.stopPropagation();
    setSelectedJobId(jobId);
    setShowUploadPopup(true);
  };

  const handleDeleteClick = (event, jobId) => {
    event.stopPropagation();
    setSelectedJobId(jobId);
    setShowDeletePopup(true);
  };

  const handleClosePopup = () => {
    setShowUploadPopup(false);
    setShowDeletePopup(false);
    setSelectedJobId(null);
  };

  // Client-side search across the most useful job fields (case-insensitive).
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredJobs = normalizedSearch
    ? jobs.filter((job) =>
        [
          job.job_title,
          job.location,
          job.experience_range,
          getJobRequirementStatus(job),
          formatJobRequirementStatus(job),
        ]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(normalizedSearch))
      )
    : jobs;

  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);
  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Skeleton shown inline inside the table — page chrome visible immediately
  if (loading) {
    return (
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 border-b border-slate-200">
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500 w-[20%]">Created on</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500 w-[25%]">Job Title</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500 w-[15%]">Status</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500 w-[25%]">Engagement</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500 w-[15%]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[1, 2, 3, 4].map(i => (
              <TableRow key={i} className="border-b border-slate-100">
                <TableCell><div className="h-4 bg-slate-100 rounded animate-pulse w-28" /></TableCell>
                <TableCell>
                  <div className="h-4 bg-slate-100 rounded animate-pulse w-40 mb-1" />
                  <div className="h-3 bg-slate-100 rounded animate-pulse w-24" />
                </TableCell>
                <TableCell><div className="h-5 bg-slate-100 rounded-full animate-pulse w-16" /></TableCell>
                <TableCell>
                  <div className="h-3 bg-slate-100 rounded animate-pulse w-20 mb-1" />
                  <div className="h-3 bg-slate-100 rounded animate-pulse w-20 mb-1" />
                  <div className="h-3 bg-slate-100 rounded animate-pulse w-28" />
                </TableCell>
                <TableCell><div className="h-5 bg-slate-100 rounded animate-pulse w-20" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  const convertToIST = (gmtDate) => {
    const date = new Date(gmtDate);
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };
    return date.toLocaleString('en-IN', options);
  };

  return (
    <>
      <div className="mb-4 flex items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            type="text"
            placeholder="Search jobs by title, location, or status…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        {normalizedSearch && (
          <span className="text-sm text-slate-500 whitespace-nowrap">
            {filteredJobs.length} result{filteredJobs.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 border-b border-slate-200">
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500 w-[20%]">
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  Created on
                </div>
              </TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500 w-[25%]">
                <div className="flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" />
                  Job Title
                </div>
              </TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500 w-[15%]">Status</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500 w-[25%]">Engagement</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500 w-[15%]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentJobs.map((job, index) => (
              <TableRow
                key={index}
                className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer"
                onClick={() => handleJobClick(job)}
              >
                <TableCell className="text-slate-600 text-sm">
                  {convertToIST(job.created_at)}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-semibold text-slate-800 text-sm">{job.job_title}</span>
                    <span className="text-slate-400 text-xs mt-1">
                      {job.experience_range} - {job.location}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={`font-semibold ${
                      ROLE_STATUS_BADGE_CLASS[getJobRequirementStatus(job)] ||
                      ROLE_STATUS_BADGE_CLASS.active
                    }`}
                  >
                    {formatJobRequirementStatus(job)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col text-sm">
                    <span className="text-blue-500 cursor-pointer mt-0.5">{job.applicants || 0} Views</span>
                    <span className="text-blue-500 cursor-pointer mt-0.5">{job.applicants || 0} Applied</span>
                    <span className="text-blue-500 cursor-pointer mt-0.5">
                      {job.applicants || 0} Recommended Candidates
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                    {[1, 2, 3, 4, 5, 6].map((_, idx) => (
                      <img
                        key={idx}
                        className="w-5 h-5 cursor-pointer hover:scale-110 transition-transform"
                        src={`https://cdn.builder.io/api/v1/image/assets/TEMP/${getActionIconUrl(idx)}`}
                        alt="Action"
                        onClick={(event) => {
                          if (idx === 2) {
                            handleUploadClick(event, job.job_id);
                          } else if (idx === 5) {
                            handleDeleteClick(event, job.job_id);
                          }
                        }}
                        title={
                          idx === 0
                            ? "Edit"
                            : idx === 1
                              ? "Duplicate"
                              : idx === 2
                                ? "Upload Resume"
                                : idx === 3
                                  ? "Save Job"
                                  : idx === 4
                                    ? "Download"
                                    : "Delete Job"
                        }
                      />
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {currentJobs.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-slate-400 py-10 text-sm">
                  {normalizedSearch
                    ? `No jobs match "${searchTerm.trim()}".`
                    : "No jobs posted yet."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <div className="flex items-center justify-center gap-2 mt-5 pb-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="text-slate-600"
          >
            ←
          </Button>

          <span className="px-3 py-1.5 text-sm rounded-lg bg-[#4c00ff] text-white border border-[#4c00ff]">
            {currentPage} / {Math.max(totalPages, 1)}
          </span>

          <Button
            variant="outline"
            size="sm"
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className="text-slate-600"
          >
            →
          </Button>
        </div>

        {showUploadPopup && (
          <UploadPopup jobId={selectedJobId} onClose={handleClosePopup} />
        )}

        <Dialog open={showDeletePopup} onOpenChange={(open) => !open && handleClosePopup()}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Delete Job
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600">
              All data regarding tests and results of candidates will be permanently lost. Are you sure you want to delete this job?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={handleClosePopup}>Cancel</Button>
              <Button variant="destructive" className="gap-2">
                <Trash2 className="w-4 h-4" /> Delete Job
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

export default JobsTable;
